import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { PageHeader } from '../components/layout/PageHeader';
import { PageLoader } from '../components/shared/LoadingSpinner';
import { StatusBadge } from '../components/shared/StatusBadge';
import { dashboardAPI, frameworksAPI } from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { 
  Shield, 
  FileCheck, 
  AlertTriangle, 
  FileText,
  ArrowRight,
  Plus,
  TrendingUp,
  Target,
  Clock,
  Award,
  Download,
  Settings2,
  GripVertical,
  Eye,
  EyeOff,
  X,
  Layers
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  PieChart, 
  Pie, 
  Cell, 
  ResponsiveContainer, 
  Tooltip,
  Legend,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  AreaChart,
  Area
} from 'recharts';
import { toast } from 'sonner';
import api from '../lib/api';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = {
  compliant: '#10b981',
  pending: '#64748b',
  gap: '#ef4444',
};

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [enhancedStats, setEnhancedStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [showWidgetPanel, setShowWidgetPanel] = useState(false);
  const [widgetConfig, setWidgetConfig] = useState(null);
  const [ctemData, setCtemData] = useState(null);

  useEffect(() => {
    fetchData();
    fetchWidgetConfig();
    fetchCTEM();
  }, []);

  const fetchWidgetConfig = async () => {
    try {
      const res = await api.get('/dashboard/widgets');
      setWidgetConfig(res.data.widgets);
    } catch {
      // Use defaults if endpoint unavailable
    }
  };

  const isWidgetEnabled = (widgetId) => {
    if (!widgetConfig) return true;
    const widget = widgetConfig.find(w => w.id === widgetId);
    return widget ? widget.enabled : true;
  };

  const toggleWidget = async (widgetId) => {
    const updated = (widgetConfig || []).map(w =>
      w.id === widgetId ? { ...w, enabled: !w.enabled } : w
    );
    setWidgetConfig(updated);
    try {
      await api.put('/dashboard/widgets', { widgets: updated });
    } catch {
      toast.error('Failed to save widget preferences');
    }
  };

  const fetchCTEM = async () => {
    try {
      const token = localStorage.getItem('grc_token');
      const res = await axios.get(`${API_URL}/api/ctem/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCtemData(res.data);
    } catch {
      // CTEM data not available yet
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, enhancedRes, activitiesRes] = await Promise.all([
        dashboardAPI.getStats(),
        api.get('/dashboard/enhanced-stats'),
        dashboardAPI.getRecentActivities(),
      ]);
      setStats(statsRes.data);
      setEnhancedStats(enhancedRes.data);
      setActivities(activitiesRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedSOC2 = async () => {
    setSeeding(true);
    try {
      const response = await frameworksAPI.seedSOC2();
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to seed SOC 2 framework');
    } finally {
      setSeeding(false);
    }
  };

  const handleDownloadReport = async () => {
    setDownloadingReport(true);
    try {
      const response = await api.get('/reports/compliance-summary', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `compliance-report-${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Report downloaded successfully');
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setDownloadingReport(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <PageLoader />
      </DashboardLayout>
    );
  }

  const complianceData = enhancedStats ? [
    { name: 'Compliant', value: enhancedStats.compliant_controls, color: COLORS.compliant },
    { name: 'Pending', value: enhancedStats.pending_controls, color: COLORS.pending },
    { name: 'Gap', value: enhancedStats.gap_controls, color: COLORS.gap },
  ].filter(d => d.value > 0) : [];

  const riskData = enhancedStats ? [
    { name: 'High', value: enhancedStats.high_risks, color: '#ef4444' },
    { name: 'Medium', value: enhancedStats.medium_risks, color: '#f59e0b' },
    { name: 'Low', value: enhancedStats.low_risks, color: '#10b981' },
  ].filter(d => d.value > 0) : [];

  // Get compliance score color
  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-emerald-50 border-emerald-200';
    if (score >= 60) return 'bg-amber-50 border-amber-200';
    return 'bg-red-50 border-red-200';
  };

  return (
    <DashboardLayout>
      <PageHeader 
        title="Dashboard" 
        description="Overview of your compliance posture"
        actions={
          <div className="flex gap-3">
            <Button
              data-testid="customize-dashboard-btn"
              variant="outline"
              onClick={() => setShowWidgetPanel(!showWidgetPanel)}
              className="rounded-sm"
            >
              <Settings2 size={18} className="mr-2" />
              Customize
            </Button>
            <Button
              data-testid="download-report-btn"
              variant="outline"
              onClick={handleDownloadReport}
              disabled={downloadingReport}
              className="rounded-sm"
            >
              <Download size={18} className="mr-2" />
              {downloadingReport ? 'Generating...' : 'Download Report'}
            </Button>
            {enhancedStats?.total_controls === 0 && (
              <Button
                data-testid="seed-soc2-btn"
                onClick={handleSeedSOC2}
                disabled={seeding}
                className="bg-blue-600 hover:bg-blue-700 rounded-sm"
              >
                <Plus size={18} className="mr-2" />
                {seeding ? 'Loading...' : 'Load SOC 2 Framework'}
              </Button>
            )}
          </div>
        }
      />

      {/* Widget Customization Panel */}
      {showWidgetPanel && widgetConfig && (
        <div className="mx-6 md:mx-8 lg:mx-12 mt-4 mb-0">
          <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-5" data-testid="widget-config-panel">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <Settings2 size={16} /> Customize Dashboard Widgets
              </h3>
              <button onClick={() => setShowWidgetPanel(false)} className="text-slate-400 hover:text-slate-600">
                <X size={18} />
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {widgetConfig.map((widget) => (
                <button
                  key={widget.id}
                  onClick={() => toggleWidget(widget.id)}
                  data-testid={`widget-toggle-${widget.id}`}
                  className={`flex items-center gap-2.5 p-3 rounded-md border text-left text-sm transition-all ${
                    widget.enabled
                      ? 'border-blue-200 bg-blue-50/50 text-blue-900'
                      : 'border-slate-200 bg-slate-50/50 text-slate-500'
                  }`}
                >
                  <GripVertical size={14} className="text-slate-300 flex-shrink-0" />
                  {widget.enabled ? <Eye size={14} className="text-blue-500 flex-shrink-0" /> : <EyeOff size={14} className="flex-shrink-0" />}
                  <span className="truncate">{widget.title}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-8 md:px-8 lg:px-12">
        {/* Compliance Score Hero Card */}
        {enhancedStats && isWidgetEnabled('compliance_score') && (
          <div className={`rounded-sm border p-6 mb-8 ${getScoreBg(enhancedStats.compliance_score)}`}>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              {/* Score Display */}
              <div className="flex items-center gap-6">
                <div className="relative">
                  <div className={`w-32 h-32 rounded-full border-8 ${enhancedStats.compliance_score >= 80 ? 'border-emerald-500' : enhancedStats.compliance_score >= 60 ? 'border-amber-500' : 'border-red-500'} flex items-center justify-center bg-white`}>
                    <div className="text-center">
                      <span className={`text-4xl font-bold ${getScoreColor(enhancedStats.compliance_score)}`}>
                        {Math.round(enhancedStats.compliance_score)}
                      </span>
                      <p className="text-xs text-slate-500 font-mono">/ 100</p>
                    </div>
                  </div>
                  <div className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow-sm">
                    <Award size={24} className={getScoreColor(enhancedStats.compliance_score)} />
                  </div>
                </div>
                <div>
                  <h2 className="font-secondary font-bold text-2xl text-slate-900">
                    Compliance Score
                  </h2>
                  <p className="text-slate-600 mt-1">
                    {enhancedStats.compliance_score >= 80 ? 'Excellent - Your organization is highly compliant' :
                     enhancedStats.compliance_score >= 60 ? 'Good - Some areas need attention' :
                     'Needs Improvement - Critical gaps identified'}
                  </p>
                </div>
              </div>

              {/* Score Breakdown */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <ScoreBreakdownItem 
                  label="Controls" 
                  score={enhancedStats.score_breakdown.controls} 
                  weight="40%"
                />
                <ScoreBreakdownItem 
                  label="Evidence" 
                  score={enhancedStats.score_breakdown.evidence} 
                  weight="20%"
                />
                <ScoreBreakdownItem 
                  label="Risk Mgmt" 
                  score={enhancedStats.score_breakdown.risk_management} 
                  weight="20%"
                />
                <ScoreBreakdownItem 
                  label="Policies" 
                  score={enhancedStats.score_breakdown.policy_coverage} 
                  weight="20%"
                />
              </div>
            </div>
          </div>
        )}

        {/* Time to Compliance Card */}
        {enhancedStats && enhancedStats.time_to_compliance.controls_remaining > 0 && (
          <div className="bg-white border border-slate-200 rounded-sm p-6 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Clock size={24} className="text-blue-600" />
              <h3 className="font-secondary font-semibold text-lg text-slate-900">
                Time to Full Compliance
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-slate-500 font-mono uppercase">Remaining Controls</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">
                  {enhancedStats.time_to_compliance.controls_remaining}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500 font-mono uppercase">Estimated Completion</p>
                <p className="text-3xl font-bold text-blue-600 mt-1">
                  {Math.ceil(enhancedStats.time_to_compliance.estimated_days / 7)} weeks
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500 font-mono uppercase">Target Date</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">
                  {new Date(Date.now() + enhancedStats.time_to_compliance.estimated_days * 86400000).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                </p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-slate-100">
              <p className="text-sm text-slate-500">
                <TrendingUp size={14} className="inline mr-1" />
                Current velocity: ~{Math.round(7 / 3.5)} controls/week
              </p>
            </div>
          </div>
        )}

        {/* CTEM Threat Exposure Widget */}
        {ctemData && ctemData.total_exposures > 0 && (
          <div className="mb-8" data-testid="ctem-dashboard-widget">
            <Link to="/ctem" className="block">
              <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 rounded-xl p-6 text-white hover:shadow-xl transition-shadow">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                      <Layers size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-base">Threat Exposure Management</h3>
                      <p className="text-xs text-slate-400">CTEM Lifecycle Status</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-3xl font-bold ${
                      ctemData.exposure_score > 7 ? 'text-red-400' :
                      ctemData.exposure_score > 4 ? 'text-amber-400' : 'text-emerald-400'
                    }`}>
                      {ctemData.exposure_score}
                    </p>
                    <p className="text-[10px] text-slate-400 uppercase tracking-wide">Exposure Score</p>
                  </div>
                </div>

                {/* Phase pipeline */}
                <div className="flex gap-1 mb-4">
                  {[
                    { key: 'discovery', label: 'Discover', color: 'bg-blue-500' },
                    { key: 'prioritization', label: 'Prioritize', color: 'bg-amber-500' },
                    { key: 'validation', label: 'Validate', color: 'bg-purple-500' },
                    { key: 'mobilization', label: 'Mobilize', color: 'bg-orange-500' },
                    { key: 'closed', label: 'Closed', color: 'bg-emerald-500' },
                  ].map((phase) => {
                    const count = ctemData.phases?.[phase.key] || 0;
                    return (
                      <div key={phase.key} className="flex-1 text-center">
                        <div className={`h-1.5 rounded-full ${phase.color} mb-1.5`} style={{ opacity: count > 0 ? 1 : 0.2 }} />
                        <p className="text-sm font-bold">{count}</p>
                        <p className="text-[9px] text-slate-400">{phase.label}</p>
                      </div>
                    );
                  })}
                </div>

                {/* Bottom stats */}
                <div className="flex items-center justify-between pt-3 border-t border-white/10 text-xs">
                  <div className="flex gap-4">
                    <span><span className="text-red-400 font-bold">{ctemData.severities?.critical || 0}</span> Critical</span>
                    <span><span className="text-orange-400 font-bold">{ctemData.severities?.high || 0}</span> High</span>
                    <span><span className="text-amber-300 font-bold">{ctemData.severities?.medium || 0}</span> Medium</span>
                  </div>
                  <div className="flex items-center gap-1 text-slate-400">
                    View CTEM <ArrowRight size={12} />
                  </div>
                </div>
              </div>
            </Link>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={Shield}
            label="Controls"
            value={enhancedStats?.total_controls || 0}
            subValue={`${enhancedStats?.compliance_percentage || 0}% compliant`}
            iconColor="text-blue-600"
            bgColor="bg-blue-50"
            testId="stat-controls"
          />
          <StatCard
            icon={FileCheck}
            label="Evidence Items"
            value={enhancedStats?.total_evidence || 0}
            subValue="collected"
            iconColor="text-emerald-600"
            bgColor="bg-emerald-50"
            testId="stat-evidence"
          />
          <StatCard
            icon={AlertTriangle}
            label="Active Risks"
            value={enhancedStats?.total_risks || 0}
            subValue={`${enhancedStats?.high_risks || 0} high priority`}
            iconColor="text-amber-600"
            bgColor="bg-amber-50"
            testId="stat-risks"
          />
          <StatCard
            icon={FileText}
            label="Policies"
            value={enhancedStats?.total_policies || 0}
            subValue={`${enhancedStats?.active_policies || 0} active`}
            iconColor="text-violet-600"
            bgColor="bg-violet-50"
            testId="stat-policies"
          />
        </div>

        {/* Framework Scores */}
        {enhancedStats && enhancedStats.framework_scores.length > 0 && isWidgetEnabled('framework_scores') && (
          <div className="bg-white border border-slate-200 rounded-sm p-6 mb-8">
            <h3 className="font-secondary font-semibold text-lg text-slate-900 mb-4">
              Framework Compliance
            </h3>
            <div className="space-y-4">
              {enhancedStats.framework_scores.map((fw) => (
                <div key={fw.framework_id} className="flex items-center gap-4">
                  <div className="w-40 flex-shrink-0">
                    <p className="font-medium text-sm text-slate-900 leading-tight">{fw.framework_name || fw.framework_id}</p>
                    <p className="text-xs text-slate-500">{fw.total_controls} controls</p>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-slate-600">
                        {fw.compliant} / {fw.total_controls} controls
                      </span>
                      <span className={`text-sm font-medium ${
                        fw.score >= 80 ? 'text-emerald-600' :
                        fw.score >= 50 ? 'text-amber-600' : 'text-red-600'
                      }`}>
                        {fw.score}%
                      </span>
                    </div>
                    <Progress value={fw.score} className="h-2" />
                  </div>
                  <StatusBadge status={fw.score >= 80 ? 'compliant' : fw.score > 0 ? 'pending' : 'gap'} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Compliance Trend */}
          {enhancedStats && enhancedStats.compliance_trend.length > 0 && isWidgetEnabled('compliance_trend') && (
            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <h3 className="font-secondary font-semibold text-lg text-slate-900 mb-4">
                Compliance Trend (6 Weeks)
              </h3>
              <div style={{ width: '100%', height: 256, minWidth: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={enhancedStats.compliance_trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Area 
                      type="monotone" 
                      dataKey="score" 
                      stroke="#2563eb" 
                      fill="#3b82f6" 
                      fillOpacity={0.2}
                      name="Compliance Score"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Risk Distribution Chart */}
          <div className="bg-white border border-slate-200 rounded-sm p-6">
            <h3 className="font-secondary font-semibold text-lg text-slate-900 mb-4">
              Risk Distribution
            </h3>
            {riskData.length > 0 ? (
              <div style={{ width: '100%', height: 256, minWidth: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={riskData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {riskData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-400">
                <p>No risks logged yet. Add a risk to see distribution.</p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions & Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Quick Actions */}
          <div className="bg-white border border-slate-200 rounded-sm p-6">
            <h3 className="font-secondary font-semibold text-lg text-slate-900 mb-4">
              Quick Actions
            </h3>
            <div className="space-y-3">
              <QuickActionLink 
                to="/evidence" 
                label="Add Evidence" 
                icon={FileCheck}
                testId="quick-add-evidence"
              />
              <QuickActionLink 
                to="/risks" 
                label="Log New Risk" 
                icon={AlertTriangle}
                testId="quick-add-risk"
              />
              <QuickActionLink 
                to="/policies" 
                label="Create Policy" 
                icon={FileText}
                testId="quick-add-policy"
              />
              <QuickActionLink 
                to="/controls" 
                label="Review Controls" 
                icon={Shield}
                testId="quick-review-controls"
              />
            </div>
          </div>

          {/* Recent Activity */}
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm p-6">
            <h3 className="font-secondary font-semibold text-lg text-slate-900 mb-4">
              Recent Activity
            </h3>
            {activities.length > 0 ? (
              <div className="space-y-4">
                {activities.slice(0, 5).map((activity, index) => (
                  <ActivityItem key={activity.id || `activity-${index}`} activity={activity} />
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <Clock size={32} className="mx-auto mb-2 opacity-50" />
                <p>No recent activity</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

const ScoreBreakdownItem = ({ label, score, weight }) => {
  const displayScore = typeof score === 'number' && !isNaN(score) ? Math.round(score) : 0;
  return (
    <div className="bg-white rounded-sm p-3 border border-slate-200">
      <p className="text-xs text-slate-500 font-mono">{label} ({weight})</p>
      <p className={`text-xl font-bold ${
        displayScore >= 80 ? 'text-emerald-600' : displayScore >= 60 ? 'text-amber-600' : 'text-red-600'
      }`}>
        {displayScore}%
      </p>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, subValue, iconColor, bgColor, testId }) => (
  <div className="stat-card" data-testid={testId}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm font-medium text-slate-500 uppercase tracking-wider font-mono">
          {label}
        </p>
        <p className="mt-2 text-3xl font-secondary font-bold text-slate-900">
          {value}
        </p>
        <p className="mt-1 text-sm text-slate-500">{subValue}</p>
      </div>
      <div className={`p-3 rounded-sm ${bgColor}`}>
        <Icon size={24} className={iconColor} />
      </div>
    </div>
  </div>
);

const QuickActionLink = ({ to, label, icon: Icon, testId }) => (
  <Link
    to={to}
    data-testid={testId}
    className="flex items-center justify-between p-3 border border-slate-200 rounded-sm hover:bg-slate-50 hover:border-blue-200 transition-all group"
  >
    <div className="flex items-center gap-3">
      <Icon size={18} className="text-slate-400 group-hover:text-blue-600" />
      <span className="text-sm font-medium text-slate-700">{label}</span>
    </div>
    <ArrowRight size={16} className="text-slate-300 group-hover:text-blue-600" />
  </Link>
);

const ActivityItem = ({ activity }) => {
  const getIcon = () => {
    switch (activity.type) {
      case 'evidence':
        return <FileCheck size={16} className="text-emerald-600" />;
      case 'risk':
        return <AlertTriangle size={16} className="text-amber-600" />;
      default:
        return <Clock size={16} className="text-slate-400" />;
    }
  };

  return (
    <div className="flex items-start gap-3 pb-4 border-b border-slate-100 last:border-0 last:pb-0">
      <div className="mt-0.5">{getIcon()}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-700 truncate">{activity.title}</p>
        <p className="text-xs text-slate-400 mt-0.5">
          {formatDateTime(activity.timestamp)}
        </p>
      </div>
      <StatusBadge status={activity.status} />
    </div>
  );
};
